import { Controller } from '@hotwired/stimulus'
import { WebRTCPlayer } from '@eyevinn/webrtc-player'
import toastr from '../src/init/toastr'

const VIDEO_PLAYBACK_TYPE = 'whep'
const STATUS_TYPE_FILTER = '^candidate-*|^inbound-rtp'
const NO_MEDIA_TIMEOUT = 5000 // If no media is received within this time after connection, we consider the stream offline. This is to handle the case where the stream is not actually offline, but there is a delay in receiving the media.
const FRAME_RELOAD_BASE_DELAY = 3000 // Cloudflare rejects the whep request (409) until the hub is publishing to the live input, so the first retry comes soon and later ones back off.
const FRAME_RELOAD_MAX_DELAY = 30000

const streamConnectedEvents = ['media-recovered', 'peer-connection-connected']
const streamDisconnectedEvents = ['peer-connection-failed', 'initial-connection-failed', 'connect-error', 'no-media']

export default class extends Controller {
  static targets = [
    'videoPlayer',
    'frameRate',
    'packetsLost',
    'frameDropped',
    'liveStatus'
  ]

  static values = {
    whepUrl: String,
    iceServers: Array,
    streamName: String,
    reloadFrame: Boolean
  }

  configPlayerEventListeners () {
    streamConnectedEvents.forEach(event => {
      this.player.on(event, () => {
        this.cancelFrameReload()

        if (this.hasLiveStatusTarget) {
          this.liveStatusTarget.textContent = 'LIVE'
          this.liveStatusTarget.classList.remove('badge-light')
          this.liveStatusTarget.classList.add('badge-danger')
        }
      })
    })

    streamDisconnectedEvents.forEach(event => {
      this.player.on(event, () => {
        this.scheduleFrameReload()

        if (this.hasLiveStatusTarget) {
          this.liveStatusTarget.textContent = 'OFFLINE'
          this.liveStatusTarget.classList.remove('badge-danger')
          this.liveStatusTarget.classList.add('badge-light')
        }
      })
    })

    this.player.on('stats:inbound-rtp', (report) => {
      if (report.kind !== 'video') {
        return
      }

      const framesDropped = report.framesDropped
      const fps = report.framesPerSecond
      const packetsLost = report.packetsLost

      if (fps > 0) {
        this.resetFrameReloadAttempts()
      }

      if (this.hasFrameDroppedTarget) {
        this.frameDroppedTarget.textContent = framesDropped
      }

      if (this.hasFrameRateTarget) {
        this.frameRateTarget.textContent = fps
      }

      if (this.hasPacketsLostTarget) {
        this.packetsLostTarget.textContent = packetsLost
      }
    })
  }

  async initWebrtcPlayer () {
    this.player = new WebRTCPlayer({
      video: this.videoPlayerTarget,
      type: VIDEO_PLAYBACK_TYPE,
      iceServers: this.iceServersValue,
      statsTypeFilter: STATUS_TYPE_FILTER,
      timeoutThreshold: NO_MEDIA_TIMEOUT
    })

    this.configPlayerEventListeners()

    try {
      this.player.load(new URL(this.whepUrlValue))
    } catch (error) {
      toastr.error('Failed to load video stream. Please reload the page and try again.')
    }
  }

  get frame () {
    return this.element.closest('turbo-frame')
  }

  // The player only ever retries the whep url it was loaded with, so reloading the frame is what picks up the camera's current url with a fresh signed token.
  scheduleFrameReload () {
    const frame = this.frame

    if (!this.reloadFrameValue || !frame || this.frameReloadTimeout) {
      return
    }

    const attempts = Number(frame.dataset.whepPlayerReloadAttempts || 0)
    const delay = Math.min(FRAME_RELOAD_BASE_DELAY * 2 ** attempts, FRAME_RELOAD_MAX_DELAY)

    frame.dataset.whepPlayerReloadAttempts = attempts + 1
    this.frameReloadTimeout = setTimeout(() => {
      this.frameReloadTimeout = null
      frame.reload()
    }, delay)
  }

  cancelFrameReload () {
    clearTimeout(this.frameReloadTimeout)
    this.frameReloadTimeout = null
  }

  resetFrameReloadAttempts () {
    if (this.frame) {
      delete this.frame.dataset.whepPlayerReloadAttempts
    }
  }

  // The stats icon toggles every stats body together, so a reloaded player starts in the state the icon is moving to, even mid-transition.
  syncStatsVisibility () {
    const statsBody = this.element.querySelector('.webrtc-stats-body')
    const statsIconExpanded = document.querySelector('[data-toggle="collapse"][href=".webrtc-stats-body"][aria-expanded="true"]')

    if (statsBody && statsIconExpanded) {
      statsBody.classList.add('show')
    }
  }

  async connect () {
    this.syncStatsVisibility()
    await this.initWebrtcPlayer()
  }

  disconnect () {
    this.cancelFrameReload()

    if (this.player) {
      this.player.destroy()
    }
  }
}
